/**
 * 공지사항 목록 — 레거시 `announcements.js` 포팅
 */
import { useAnnouncements } from '@/entities/board/hooks/useBoard';
import {
  ANNOUNCEMENT_CATEGORY_FILTER_OPTIONS,
  ANNOUNCEMENT_PUBLICATION_STATUS_OPTIONS,
  computeAnnouncementPublicationStatus,
  getAnnouncementPublicationStatusBadgeVariant,
  getAnnouncementPublicationStatusLabel,
} from '@/pages/board/announcementStatusUtils';
import {
  getAnnouncementCategoryBadgeVariant,
  getAnnouncementCategoryLabel,
} from '@/pages/board/boardUtils';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  announcementEditPath,
  announcementViewPath,
  ROUTE_PATHS,
} from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { formatDateTime } from '@/shared/lib/dateFormat';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Copy, Edit, Eye, Megaphone, Plus, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const getPublicAnnouncementUrl = (id: number) => {
  const base =
    import.meta.env.VITE_PUBLIC_APP_URL?.replace(/\/$/, '') ??
    window.location.origin;
  return `${base}/announcements/${id}`;
};

const ANNOUNCEMENT_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  title: '',
  category: '',
  status: '',
};

export const AnnouncementsPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(ANNOUNCEMENT_SEARCH_DEFAULTS);

  const [titleInput, setTitleInput] = useState(values.title);
  const [categoryInput, setCategoryInput] = useState(values.category);
  const [statusFilters, setStatusFilters] = useState<string[]>(
    values.status ? values.status.split(',').filter(Boolean) : []
  );

  useEffect(() => {
    setTitleInput(values.title);
    setCategoryInput(values.category);
    setStatusFilters(values.status ? values.status.split(',').filter(Boolean) : []);
  }, [values.title, values.category, values.status]);

  const search = useMemo(
    () => ({
      title: values.title || undefined,
      category: values.category || undefined,
      publicationStatusList: statusFilters.length > 0 ? statusFilters : undefined,
    }),
    [values.title, values.category, statusFilters],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );

  const { data, isLoading, isError, error } = useAnnouncements(search, pageable);

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const rowsWithStatus = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        publicationStatus: computeAnnouncementPublicationStatus(row),
      })),
    [rows],
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      title: titleInput.trim(),
      category: categoryInput,
      status: statusFilters.join(','),
      page: 1,
    });
  };

  const toggleStatusFilter = (status: string, checked: boolean) => {
    const newFilters = checked 
      ? [...new Set([...statusFilters, status])]
      : statusFilters.filter((item) => item !== status);
    
    setStatusFilters(newFilters);
    setValues({ 
      status: newFilters.join(','), 
      page: 1 
    });
  };

  const openView = (id: number) => {
    navigate({
      to: announcementViewPath(id),
      search: { mode: 'view' },
    } as never);
  };

  const openEdit = (id: number) => {
    navigate({ to: announcementEditPath(id) } as never);
  };

  const handleCopyUrl = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = getPublicAnnouncementUrl(id);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('공지 URL을 복사했습니다.');
    } catch {
      toast.error('URL 복사에 실패했습니다.');
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Megaphone}
        title="공지사항"
        description="공지사항 조회 및 등록"
        actions={
          <MenuWriteGate>
            <Button
              onClick={() =>
                navigate({ to: ROUTE_PATHS.BOARD_ANNOUNCEMENT_NEW } as never)
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              공지 등록
            </Button>
          </MenuWriteGate>
        }
      />

      <FilterCard>
          <FilterRow gap={16} onSubmit={handleSearch}>
            <div className="space-y-1">
              <Label>제목 검색</Label>
              <Input
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                className="w-64"
                placeholder="제목으로 검색"
              />
            </div>
            <div className="space-y-1">
              <Label>카테고리</Label>
              <Select
                value={categoryInput || 'all'}
                onValueChange={(value) => {
                  const newCategory = value === 'all' ? '' : value;
                  setCategoryInput(newCategory);
                  setValues({ category: newCategory, page: 1 });
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="전체 카테고리" />
                </SelectTrigger>
                <SelectContent>
                  {ANNOUNCEMENT_CATEGORY_FILTER_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value || 'all'}
                      value={opt.value || 'all'}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
          </FilterRow>

          <div className="space-y-2">
            <Label>공개 상태</Label>
            <div className="flex flex-wrap gap-4">
              {ANNOUNCEMENT_PUBLICATION_STATUS_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={statusFilters.includes(opt.value)}
                    onChange={(e) =>
                      toggleStatusFilter(opt.value, e.target.checked)
                    }
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="공지사항을 불러오는 중..." />
          </div>
        ) : rowsWithStatus.length === 0 ? (
          <EmptyState description="공지사항이 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>구분</TableHead>
                  <TableHead>제목</TableHead>
                  <TableHead>등록일</TableHead>
                  <TableHead>게시 기간</TableHead>
                  <TableHead>공개 상태</TableHead>
                  <TableHead>조회 수</TableHead>
                  <TableHead className="w-52">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowsWithStatus.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openView(row.id)}
                  >
                    <TableCell>
                      <Badge
                        variant={getAnnouncementCategoryBadgeVariant(
                          row.category,
                        )}
                      >
                        {getAnnouncementCategoryLabel(row.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        className="text-left hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openView(row.id);
                        }}
                      >
                        {row.title}
                      </button>
                    </TableCell>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell className="text-sm">
                      {formatDateTime(row.publishedStartAt)}
                      <br />~ {formatDateTime(row.publishedEndAt)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getAnnouncementPublicationStatusBadgeVariant(
                          row.publicationStatus,
                        )}
                      >
                        {getAnnouncementPublicationStatusLabel(
                          row.publicationStatus,
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {(row.viewCount ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex flex-wrap gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleCopyUrl(row.id, e)}
                        >
                          <Copy className="mr-1 h-3 w-3" />
                          URL
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openView(row.id)}
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          보기
                        </Button>
                        <MenuWriteGate>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(row.id)}
                          >
                            <Edit className="mr-1 h-3 w-3" />
                            수정
                          </Button>
                        </MenuWriteGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>
    </div>
  );
};
